import cn from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import MainButton from '../common/util/input/MainButton'
import { formatUSD2, formatWAX, formatWAXS } from '../helpers/FormatLinks'
import { getBoostAction } from '../helpers/WaxApi'
import LoadingIndicator from '../loadingindicator'
import { useSharedState } from '../waxplorer/Waxplorer'

export const BuyofferButtons = ({
    buyoffer,
    ended,
    canceled,
    className = 'w-full h-8 mt-auto',
    transferred,
    callBack,
    isLoading,
    onAdd,
    setIsLoading,
    showOwner,
    view,
    onOpen,
    setAction,
}) => {
    const [state, { login }] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null
    const { t } = useTranslation('common')

    const { price, buyer, currency, buyofferId, collection } = buyoffer

    const { blacklisted } = collection

    const cancel = async () => {
        try {
            const result = await activeUser.session.transact(
                {
                    actions: [
                        getBoostAction(activeUser),
                        {
                            account: 'atomicmarket',
                            name: 'canceltbuyo',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data: {
                                buyoffer_id: buyofferId,
                            },
                        },
                    ],
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )
            callBack(
                {
                    success: true,
                    transactionId: result?.response?.transaction_id,
                },
                'cancel',
            )
        } catch (e) {
            console.error(e)
            callBack(
                {
                    success: false,
                    error: e.message,
                },
                'cancel',
            )
        } finally {
            setIsLoading(false)
        }
    }

    const accept = () => {
        setIsLoading(true)
        setAction('accept_buyoffer')
    }

    const buyContainerClassName = cn(
        'relative grid grid-cols-2 space-x-2 font-bold text-black dark:!text-white block w-full my-auto',
        { 'text-center': view !== 'full' },
        { 'text-left': view === 'full' },
    )
    const singleContainerClassName = cn(
        'mt-auto relative grid font-bold text-black dark:!text-white block text-center grid-cols-1 w-full my-auto',
    )
    const sellContainerClassName = cn(
        'mt-auto relative grid grid-cols-2 font-bold text-black dark:!text-white block w-full my-auto',
        { 'text-center': view !== 'full' },
        { 'text-left right-auto': view === 'full' },
    )

    const buttonClassName = cn(
        'font-bold rounded-md text-primary',
        { 'm-auto': view !== 'full' },
        { 'mr-auto': view === 'full' },
    )
    const cancelButtonClassName = cn(buttonClassName, 'm-auto w-24 text-white')

    const detailsButton =
        view === 'full' ? (
            ''
        ) : (
            <MainButton
                fullWidth={true}
                className={'font-bold rounded-md text-primary'}
                varient={'bordered'}
                onClick={onOpen}
                padding={'pr-2'}
            >
                {t('asset.details')}
            </MainButton>
        )

    const singleDetailsButton =
        view === 'full' ? (
            ''
        ) : (
            <MainButton
                fullWidth={true}
                className={'font-bold rounded-md text-primary'}
                varient={'bordered'}
                onClick={onOpen}
            >
                {t('asset.details')}
            </MainButton>
        )

    const acceptField = (
        <div className={sellContainerClassName}>
            {detailsButton}
            <MainButton
                fullWidth={true}
                className={buttonClassName}
                onClick={accept}
                padding={'pl-2'}
            >
                {t('trade.accept')}
            </MainButton>
        </div>
    )

    const detailsField = (
        <div className={singleContainerClassName}>{singleDetailsButton}</div>
    )

    const cancelField = (
        <div className={buyContainerClassName}>
            {detailsButton}
            <MainButton className={cancelButtonClassName} onClick={cancel}>
                {t('asset.cancel')}
            </MainButton>
        </div>
    )

    const loginField = (
        <div className={buyContainerClassName}>
            {detailsButton}
            <MainButton
                fullWidth={true}
                varient={'solid'}
                onClick={() => login()}
            >
                {t('navigation.login')}
            </MainButton>
        </div>
    )

    let acceptable, cancable

    if (!ended && userName && userName !== buyer && !transferred && !canceled) {
        acceptable = true
    } else if (
        userName &&
        !ended &&
        userName === buyer &&
        !canceled &&
        !transferred
    ) {
        cancable = true
    }

    return (
        <div className={cn('relative mt-4', className)}>
            <div
                className={cn(
                    'relative py-0 px-0 2xl:px-1 w-full text-base mb-6 text-zinc-900 dark:text-white',
                    'md:text-xs lg:text-base leading-5 flex-wrap text-center h-[28px] sm:h-[44px]',
                    { 'text-center': view !== 'full' },
                    { 'text-left': view === 'full' },
                )}
            >
                <div
                    className={cn(
                        'flex h-[20px] sm:h-2/3',
                        { 'justify-start mx-auto': view !== 'full' },
                        { 'justify-start mr-auto': view === 'full' },
                    )}
                >
                    {currency === 'USD' && price ? (
                        <div
                            className={
                                'text-xs md:text-base lg:text-base xl:text-xs text-right my-auto font-bold'
                            }
                        >
                            <span className="hidden sm:block">
                                <b>{formatUSD2(price)}</b>
                            </span>
                        </div>
                    ) : (
                        ''
                    )}
                    {currency === 'WAX' && price && view !== 'full' ? (
                        <div
                            className={
                                'text-xs md:text-base lg:text-base xl:text-xs text-right my-auto font-bold'
                            }
                        >
                            <span className="hidden sm:block">
                                <b>{formatWAX(price)}</b>
                            </span>
                            <span className="block sm:hidden">
                                <b>{formatWAXS(price)}</b>
                            </span>
                        </div>
                    ) : (
                        ''
                    )}
                </div>
            </div>

            <div
                className={'flex h-auto my-auto mx-auto w-full justify-center'}
            >
                {isLoading ? (
                    <LoadingIndicator
                        className={buttonClassName}
                        margin={'m-0'}
                    />
                ) : !blacklisted && !onAdd && acceptable ? (
                    acceptField
                ) : !blacklisted && !onAdd && cancable ? (
                    cancelField
                ) : !showOwner && !cancable ? (
                    detailsField
                ) : !cancable && !userName ? (
                    loginField
                ) : (
                    ''
                )}
            </div>
        </div>
    )
}

export default BuyofferButtons
